<template>
    <div :class="{'form-group': isLabelSlot,'is-invalid': validation && validation.$error}">
        <label v-if="isLabelSlot" :for="id">
            <slot name="label"/>
            <template v-if="optional">
                - <small>{{ $t('Optional') }}</small>
            </template>
        </label>
        <multiselect :id="id"
                     v-model="val"
                     :open-direction="openDir"
                     :class="{ 'is-invalid': validation && validation.$error, 'notEmpty' : notEmpty, 'sm-select' : smSelect, 'e3-searchable': searchable, 'e3-sharp': sharpCorners, 'min-width' : hasMinWidth }"
                     :placeholder="placeholder"
                     :options="isAsync ? localOptions : options"
                     :label="label"
                     :custom-label="customLabel"
                     :options-limit="optionsLimit"
                     :multiple="multiple"
                     :max="max"
                     :close-on-select="closeOnSelect"
                     :track-by="trackBy"
                     :searchable="searchable"
                     :disabled="disabled"
                     :loading="isLoading"
                     :select-label="$t('Choose')"
                     :selected-label="$t('Selected')"
                     :deselect-label="$t('Remove')"
                     :block-keys="['Delete']"
                     :show-labels="showLabels"
                     :allow-empty="!notEmpty"
                     :preselect-first="preselectFirst"
                     :internal-search="!isAsync"
                     @select="onSelect"
                     @search-change="search"
                     @remove="onRemove"
                     @open="onOpen"
                     @close="onClose">
            <template #maxElements><span>{{$t('Maximum options selected')}}</span></template>
            <template #noResult><span>{{$t('Nothing found')}}</span></template>
            <template #noOptions><span>{{$t('Type to search')}}</span></template>
            <template #tag/>
            <template v-if="flags" #singleLabel="props">
                <div class="d-flex flex-row">
                    <img v-if="getImgNameWithCode(props.option)"
                         class="max-w-20px mr-2"
                         :src=" `/images/flags/${getImgNameWithCode(props.option)}.svg`"
                         :alt="props.option"/>
                    <div class="d-inline-block mr-2">+{{ props.option }}</div>
                </div>
            </template>
            <!-- Custom look for select options. See child_development Goal.vue goal selection for example. -->
            <template v-if="flags || customOptions" #option="props">
                <div v-if="flags" class="d-flex flex-row pr-2">
                    <template v-if="props.option.code">
                        <img v-if="getImgNameWithAbbr(props.option.abbr)"
                             class="max-w-20px mr-2"
                             :src="`/images/flags/${getImgNameWithAbbr(props.option.abbr)}.svg`"
                             :alt="props.option.abbr"/>
                        <div class="d-inline-block mr-2">
                            +{{ props.option.code }}
                        </div>
                    </template>
                    <template v-else>
                        {{ props.option.abbr }}
                    </template>
                </div>
                <div v-else-if="customOptions && typeof props.option === 'object' && props.option !== null"
                     :class="props.option.classes">
                    {{ props.option.label }}
                </div>
            </template>

            <template #caret="{ toggle }">
                <div class="multiselect__select" @mousedown.prevent.stop="toggle">
                    <ChevronDownIcon class="text-primary"/>
                </div>
            </template>
        </multiselect>

        <ValitationMessages :validation="validation"/>
    </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import ChevronDownIcon from '@/common/components/svg-icons/ChevronDownIcon.vue';

export default {
    name: 'ESelect',
    props: {
        id: [String, Number],
        value: [Object, String, Number, Array],
        options: [Array, Object],
        notEmpty: Boolean,
        label: String,
        searchable: Boolean,
        flags: Boolean,
        customLabel: Function,
        optional: Boolean,
        placeholder: {
            type: String,
            default: ''
        },
        isAsync: {
            type: Boolean,
            default: false
        },
        customOptions: {
            type: Boolean,
            default: false
        },
        optionsLimit: Number,
        closeOnSelect: Boolean,
        multiple: Boolean,
        max: {
            type: Number,
            default: null
        },
        trackBy: String,
        disabled: Boolean,
        isLoading: Boolean,
        validation: Object,
        preselectFirst: Boolean,
        showLabels: {
            type: Boolean,
            default: true
        },
        smSelect: Boolean,
        openDir: {
            type: String,
            default: 'bottom'
        },
        sharpCorners: {
            type: Boolean,
            default: false
        },
        hasMinWidth: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            localOptions: []
        };
    },
    computed: {
        val: {
            get() {
                return this.value;
            },
            set(value) {
                if (this.isAsync) {
                    this.localOptions = [];
                }
                this.$emit('input', this.flags ? value.code : value);
            }
        },
        isLabelSlot() {
            return !!this.$slots.label;
        }
    },
    methods: {
        search(query) {
            if (this.isAsync) {
                if(this.flags) {
                    this.localOptions = this.options.filter(opt => {
                        return opt.code.includes(query);
                    });
                }
            }
        },
        getImgNameWithCode(code) {
            const country = this.options.find(op => op.code === code);
            if(country) {
                return country.abbr.toLowerCase();
            }
        },
        getImgNameWithAbbr(abbr) {
            return abbr.toLowerCase();
        },
        onSelect(value) {
            if (this.isAsync) {
                this.localOptions = [];
            }
            this.$emit('select', this.flags ? value.code : value);
        },
        onRemove(value) {
            this.$emit('remove', this.flags ? value.code : value);
        },
        onOpen(id) {
            this.$emit('open', id);
        },
        onClose(value) {
            this.$emit('close', this.flags ? value?.code : value);
        }
    },
    components: {
        ChevronDownIcon,
        Multiselect
    }
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
